import React from 'react';
import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DefinitionTestSuiteTrendPoint } from '../hooks/useDefinitionTestSuites';

interface Props {
  points: DefinitionTestSuiteTrendPoint[];
  loading?: boolean;
  error?: boolean;
  onOpenSuite?: (suiteId: string) => void;
}

function TrendTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: DefinitionTestSuiteTrendPoint }>;
}) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
        maxWidth: 280,
      }}
    >
      <Typography variant="body2" fontWeight="bold">
        {point.brand_name}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {point.keyword}
      </Typography>
      <Typography variant="body2">
        {point.pass_rate}% passed ({point.passed}/{point.attempted || 0})
      </Typography>
      {point.skipped > 0 && (
        <Typography variant="caption" color="text.secondary" display="block">
          {point.skipped} skipped
        </Typography>
      )}
    </Box>
  );
}

export const TestSuiteTrendChart: React.FC<Props> = ({
  points,
  loading = false,
  error = false,
  onOpenSuite,
}) => {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Test suite pass rate
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          Share of marketplaces that returned a stage 2 product. Click a point to open the report.
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" width="100%" height={300} />
        ) : error ? (
          <Box
            sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Typography color="error">Could not load test suite trend</Typography>
          </Box>
        ) : points.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={points}
              onClick={state => {
                const suiteId = state?.activePayload?.[0]?.payload?.id;
                if (suiteId && onOpenSuite) onOpenSuite(String(suiteId));
              }}
              style={{ cursor: onOpenSuite ? 'pointer' : 'default' }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis domain={[0, 100]} unit="%" width={48} />
              <RechartsTooltip content={<TrendTooltip />} />
              <Line
                type="monotone"
                dataKey="pass_rate"
                name="Pass rate"
                stroke="#1976d2"
                strokeWidth={2}
                dot={{ fill: '#1976d2', strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Typography color="textSecondary">No test suites yet</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TestSuiteTrendChart;
